import { AfterViewInit, Directive, ElementRef, HostListener, Input } from '@angular/core';

import { DocumentService, WindowService } from 'src/app/core/services/global';

@Directive({
  selector: '[appModal]',
})
export class ModalDirective implements AfterViewInit {
  constructor(
    private element: ElementRef<HTMLElement>,
    private windowService: WindowService,
    private documentService: DocumentService,
  ) {}

  @Input() staticHeight = false;
  @Input() staticTitle = false;
  @Input() staticText = false;

  @HostListener('window:resize') onResize(): void {
    if (this.staticHeight) {
      const newWindowHeight: number = this.windowService.innerHeight;

      if (newWindowHeight !== this.currentWindowHeight) {
        this.handleStatic();
      }
    }
  }

  private readonly scrollWrapperSelector = '.scroll-wrapper';
  private readonly scrollbarSelector = '.scrollbar';

  private readonly largeDisplayHeight = 850;
  private readonly mediumDisplayHeight = 750;

  private readonly modalMaxHeightVH = 90;
  private readonly modalMinHeight = 240;
  private readonly modalStaticMaxHeight = 626;
  private readonly modalStaticMaxHeightMediumScreen = 674;
  private readonly modalStaticMaxHeightLargeScreen = 722;

  private readonly actionsHeight = 48;
  private readonly titleHeight = 28;
  private readonly textHeight = 24;

  private readonly headerMarginTop = 56;
  private readonly headerMarginBottom = 24;

  private currentWindowHeight: number;

  ngAfterViewInit(): void {
    if (this.staticHeight) {
      this.handleStatic();
    }
  }

  private handleStatic(): void {
    this.setCurrentWindowHeight();

    if (this.staticHeight) {
      this.handleStaticHeightScroll();
    }
  }

  private setCurrentWindowHeight(): void {
    this.currentWindowHeight = this.windowService.innerHeight;
  }

  private handleStaticHeightScroll(): void {
    const scrollWrapper: HTMLElement = this.element.nativeElement.querySelector(
      this.scrollWrapperSelector,
    );
    const scrollbar: HTMLElement = this.element.nativeElement.querySelector(this.scrollbarSelector);

    if (scrollWrapper && scrollbar) {
      const scrollHeightToReduce: number = this.getScrollHeightToReduce();

      const scrollHeight: string = this.getScrollHeightInPx(scrollHeightToReduce);
      const scrollMaxHeight: string = this.getScrollMaxHeightInPx(scrollHeightToReduce);
      const scrollMinHeight: string = this.getScrollMinHeightInPx(scrollHeightToReduce);

      scrollbar.style.height = scrollHeight;
      scrollbar.style.maxHeight = scrollMaxHeight;
      scrollbar.style.minHeight = scrollMinHeight;

      scrollWrapper.style.height = scrollHeight;
      scrollWrapper.style.maxHeight = scrollMaxHeight;
      scrollWrapper.style.minHeight = scrollMinHeight;
    }
  }

  private getScrollHeightInPx(scrollHeightToReduce: number): string {
    const scrollHeight: number = this.getScrollHeight(scrollHeightToReduce);

    return ModalDirective.getElementSizeInPx(scrollHeight);
  }

  private getScrollMaxHeightInPx(scrollHeightToReduce: number): string {
    const scrollMaxHeight: number = this.getScrollMaxHeight(scrollHeightToReduce);

    return ModalDirective.getElementSizeInPx(scrollMaxHeight);
  }

  private getScrollMinHeightInPx(scrollHeightToReduce: number): string {
    const scrollMaxHeight: number = this.getScrollMinHeight(scrollHeightToReduce);

    return ModalDirective.getElementSizeInPx(scrollMaxHeight);
  }

  private getScrollHeightToReduce(): number {
    const headerHeight: number = this.getHeaderHeight();

    return headerHeight + this.actionsHeight;
  }

  private getScrollHeight(scrollHeightToReduce: number): number {
    const modalMaxHeight: number = this.getVHInPx(this.modalMaxHeightVH);

    return modalMaxHeight - scrollHeightToReduce;
  }

  private getScrollMaxHeight(scrollHeightToReduce: number): number {
    const modalStaticMaxHeight: number = this.getModalStaticMaxHeight();

    return modalStaticMaxHeight - scrollHeightToReduce;
  }

  private getScrollMinHeight(scrollHeightToReduce: number): number {
    return this.modalMinHeight - scrollHeightToReduce;
  }

  private getHeaderHeight(): number {
    if (this.staticTitle || this.staticHeight) {
      const titleHeight: number = this.staticTitle ? this.titleHeight : 0;
      const textHeight: number = this.staticText ? this.textHeight : 0;

      return titleHeight + textHeight + this.headerMarginTop + this.headerMarginBottom;
    }

    return 0;
  }

  private getModalStaticMaxHeight(): number {
    const windowHeight: number = this.windowService.innerHeight;
    let result: number;

    if (windowHeight >= this.largeDisplayHeight) {
      result = this.modalStaticMaxHeightLargeScreen;
    } else if (windowHeight >= this.mediumDisplayHeight) {
      result = this.modalStaticMaxHeightMediumScreen;
    } else {
      result = this.modalStaticMaxHeight;
    }

    return result;
  }

  private getVHInPx(value: number): number {
    const height: number = Math.max(
      this.documentService.elementClientHeight,
      this.windowService.innerHeight || 0,
    );

    return (value * height) / 100;
  }

  private static getElementSizeInPx(size: number): string {
    const px = 'px';

    return `${size}${px}`;
  }
}
